import { fetcher } from '@weezevent/weezjs-core';

import { SeatInventory } from './seat.js';
/**
 * Zone.
 *
 * @param {Object} params - Zone API object
 * @param {string} params.name - Name of the zone
 * @param {Object[]} params.children - Array of Zone API objects
 * @param {Object} params.parent - Zone API object
 * @param {Object} params.seatlib_data - SeatLib data object
 * @param {Object[]} params.seats - Array of Seat API objects
 * @param {string} params.uuid - UUID of the given Zone
 * @constructor
 */
export default function Zone({
    name,
    inventories = {},
    children = [],
    parent,
    seatlib_data: seatlibData,
    seat_count: seatCount,
    general_admission: generalAdmission,
    translations,
    seats,
    uuid
}) {
    let that = {};

    let zonedChildren = children.map(child => new Zone(child));
    let zonedParent = parent && parent instanceof Object ? new Zone(parent) : null;

    that.name = () => name;
    that.categories = inventory => {
        if (!inventories[inventory]) {
            return [];
        }

        return inventories[inventory].categories;
    };

    that.inventories = () => inventories;

    that.children = () => zonedChildren;
    that.parent = () => zonedParent;
    that.parentId = () => {
        if (that.parent()) {
            return that.parent().id();
        }

        if (parent) {
            return parent;
        }

        return null;
    };
    that.seatCount = (zones = null) => {
        // If zones is provided, aggregate seatCount by counting children values
        let childrenSeatCount = 0;
        if (zones) {
            let childZones = zones.filter(zone => zone.parentId() === that.id());
            childrenSeatCount = childZones.reduce((acc, zone) => (acc += zone.seatCount()), 0);
        }
        return seatCount + childrenSeatCount;
    };
    that.generalAdmissionCount = (zones = null) => {
        // If zones is provided, aggregate generalAdmissionCount by counting children values
        let childrenGeneralAdmissionCount = 0;
        if (zones) {
            let childZones = zones.filter(zone => zone.parentId() === that.id());
            childrenGeneralAdmissionCount = childZones.reduce((acc, zone) => (acc += zone.generalAdmissionCount()), 0);
        }
        return (generalAdmission ? generalAdmission.capacity : 0) + childrenGeneralAdmissionCount;
    };
    that.seatlibData = () => seatlibData;
    that.seats = () => (seats ? seats.map(seat => new SeatInventory(seat)) : []);
    that.id = () => uuid;
    that.generalAdmission = () => generalAdmission;
    that.translations = () => translations;

    that.capacity = zones => {
        if (that.seatCount()) {
            return that.seatCount();
        }

        let children = zones.filter(zone => zone.parentId() === that.id());
        if (children && children.length) {
            return children.reduce((accumulator, zone) => accumulator + (zone.seatCount() ? zone.seatCount() : 0), 0);
        }

        return null;
    };

    return that;
}

Zone.get = ({ organizationId, chartId, id }, query = {}) => {
    let encode = encodeURIComponent;
    let queryString = Object.entries(query)
        .map(([key, value]) => `${encode(key)}=${encode(value)}`)
        .join('&');
    queryString = queryString ? `?${queryString}` : '';
    return fetcher.get(`/ticket/organizations/${organizationId}/charts/${chartId}/zones/${id}${queryString}`).then(({ response }) => Zone.fromJSON(response));
};

Zone.getAll = ({ organizationId, chartId }) => {
    return fetcher.get(`/ticket/organizations/${organizationId}/charts/${chartId}/zones`).then(({ response }) => Zone.fromAPI(response));
};

/**
 * Simple sort function that sorts an array of Zones by alphabtical order on their names
 * @param {Zone[]} zones - List of zones to sort
 * @returns {Zone[]} List of sorted Zones
 */
Zone.sortByName = zones => {
    zones.sort((zoneA, ZoneB) => {
        if (zoneA.name() < ZoneB.name()) return -1;
        if (zoneA.name() > ZoneB.name()) return 1;
        return 0;
    });
    return zones;
};

/**
 * Create Zones from API return
 * @param {Object[]} data - Data returned from API
 * @returns {Zone[]} List of new Zones
 */
Zone.fromAPI = data => data.map(zone => new Zone(zone));
Zone.list = Zone.fromAPI;

Zone.fromJSON = json => new Zone(json);

Zone.fromSeatlib = zone => {
    let data = zone.toJSON();
    data.uuid = zone.id();
    data.seatlib_data = zone.seatlibData();
    data.categories = zone.categories() || [];
    return new Zone(data);
};
